import { memo, useMemo, useState, useCallback } from 'react';
import { subDays } from 'date-fns';
import { useMount } from 'react-use';
import { Box, Grid, Switch, Typography, Button } from '@mui/material';

import { PRODUCT_TUTORIALS } from 'common/constants';
import DatePickerMenu from 'components/DatePickerMenu';
import HistoricViewToggleButton from 'components/HistoricViewToggleButton';
import GraphBarIcon from 'components/Icons/GraphBarIcon';
import TableViewIcon from 'components/Icons/TableViewIcon';
import Information from 'components/Information';
import classNames from 'classnames';

import styles from './GraphHeader.module.scss';

const switchStyles = {
  width: '200px',
  '& .MuiSwitch-thumb': {
    width: '100px',
  },
  '& .Mui-checked': {
    left: '36px',
  },
};

const lotusPreviewText =
  'our advanced surf forecasting system designed specifically for surfers...';

interface Props {
  hasHistoricForecastPermissions: boolean;
  isHistoricView: boolean;
  isTableViewMode: boolean;
  setStartDate: (newStartDate: Date | null) => void;
  spotId: string;
  spotName: string;
  startDate?: string;
  subregionId?: string;
  subregionName?: string;
  toggleHistoricView: () => void;
  toggleTableView: () => void;
}

const GraphHeader: React.FC<Props> = ({
  hasHistoricForecastPermissions,
  isHistoricView,
  isTableViewMode,
  setStartDate,
  spotId,
  spotName,
  startDate,
  subregionId,
  subregionName,
  toggleHistoricView,
  toggleTableView,
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const onClickLotusShowMoreHandler = useCallback(() => setShowFull(true), [setShowFull]);
  const onClickLotusHideMoreHandler = useCallback(() => setShowFull(false), [setShowFull]);

  useMount(() => setIsMounted(true));

  // Specific text for OB SD (sandbox and production) id.
  // We are testing if spot optimized text improves ranking. If it does we'll add this to the CMS.
  const lotusFullText = useMemo(
    () =>
      spotId === '5842041f4e65fad6a770883f' || spotId === '5842041f4e65fad6a770883f'
        ? `our advanced surf forecasting system designed specifically for surfers,
      providing precise, localized predictions tailored to the unique conditions
      of Ocean Beach, San Diego. Unlike general ocean models, LOTUS surf forecast
      model focuses on nearshore dynamics, leveraging high-resolution bathymetry to capture
      how diverse swells—such as W, NW, SW, and SSW—interact with the
      sand-bottomed breaks at OB. By incorporating 25 years of surf reports,
      20 years of camera data, and real-time observations from satellites,
      buoys, and Surfline forecasters, LOTUS dynamically updates surf forecasts,
      correcting errors as conditions evolve. For Ocean Beach, LOTUS offers
      crucial insights into the area's complex surf setup, including
      spots like Big Jetty with its fast, hollow rights, and Avalanche's
      consistent lefts.`
        : `our advanced surf forecasting system designed specifically for surfers,
      offering precise, localized predictions by focusing on nearshore conditions
      and not just deep-water swells. Using high-resolution bathymetry, 25 years of
      surf reports, and 20 years of camera data, LOTUS leverages machine learning and
      real-time observations from satellites, buoys, and forecasters to continuously
      refine its accuracy. Unlike static surf forecast models, LOTUS updates
      forecasts dynamically, correcting errors as conditions evolve. Tailored
      calibrations for over 5,000 spots worldwide ensure reliable predictions for
      all surf heights, from small waves to major swells. By combining cutting-edge
      technology with decades of surf expertise, LOTUS sets a new standard for surf
      forecasting accuracy.`,
    [spotId],
  );

  const historicForecastSegmentCTAprops = useMemo(
    () => ({
      location: 'historical forecast - pop up',
      category: 'cams & reports',
      pageName: 'Spot Report',
      spotId,
      spotName,
    }),
    [spotId, spotName],
  );

  const maxDate = useMemo(() => subDays(new Date(), 1), []);
  const minDate = useMemo(() => new Date(2010, 0, 1), []);

  const informationSegmentProps = useMemo(
    () => ({
      modalType: 'tutorial',
      spotId,
      spotName,
      subregionId,
      subregionName,
      tabName: 'report',
      uiElement: 'lotus-information',
    }),
    [spotId, spotName, subregionId, subregionName],
  );

  const inputProps = useMemo(
    () => ({
      id: 'forecast-graph-type-toggle-input',
      'aria-labelledby': isTableViewMode
        ? 'forecast-view-toggle-graph-view-label'
        : 'forecast-view-toggle-table-view-label',
    }),
    [isTableViewMode],
  );

  return (
    <Box className={styles.headerWrapper}>
      <Grid
        container
        justifyContent="space-between"
        className={styles.pageHeading}
        data-testid="graph-header"
      >
        <Grid item>
          <Typography className={styles.surfForecastHeader} variant="title3">
            {spotName} Surf Forecast
            <Information
              articleId={PRODUCT_TUTORIALS.articles.lotus.id}
              segmentProps={informationSegmentProps}
              tooltip={PRODUCT_TUTORIALS.articles.lotus.tooltip}
              tutorialId={PRODUCT_TUTORIALS.tutorials.default.id}
            />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <span style={{ fontWeight: 'bold' }}>LOTUS Model</span>
            {showFull ? (
              <>
                <span> - {lotusFullText}</span>
                <Button
                  variant="text"
                  onClick={onClickLotusHideMoreHandler}
                  sx={{ ml: 1 }}
                  className={styles.collapseLink}
                >
                  Collapse
                </Button>
              </>
            ) : (
              <>
                <span> - {lotusPreviewText}</span>
                <Button
                  variant="text"
                  onClick={onClickLotusShowMoreHandler}
                  sx={{ ml: 1 }}
                  className={styles.collapseLink}
                >
                  Read More
                </Button>
              </>
            )}
          </Typography>
        </Grid>
        <Grid item flex={1}>
          <Box className={styles.headerButtons}>
            <HistoricViewToggleButton
              isHistoricView={isHistoricView}
              toggleHistoricView={toggleHistoricView}
              tooltipText={isHistoricView ? 'Return to current forecast' : 'Historic Surf Forecast'}
              isPermitted={hasHistoricForecastPermissions}
              segmentPropertiesCTA={historicForecastSegmentCTAprops}
            />
            {/* Need to rerender on mount since isTableViewMode is potentially coming from localstorage */}
            {/* the change of isTableViewMode between getInitialProps and the page mount does not trigger a rerender of this UI */}
            {isMounted && (
              <Box
                className={classNames({
                  [styles.switchRoot]: true,
                  [styles.withText]: true,
                })}
                id="graph-header-forecast-view-toggle"
              >
                <Switch
                  checked={isTableViewMode}
                  color="default"
                  onChange={toggleTableView}
                  data-testid="forecast-graph-type-toggle"
                  variant="outlined"
                  sx={switchStyles}
                  inputProps={inputProps}
                />
                <Box className={styles.switchIconLeft} id="forecast-view-toggle-table-view-label">
                  <TableViewIcon />
                  <Typography
                    component="span"
                    className={styles.switchText}
                    data-testid="table-view-icon-text"
                  >
                    Table
                  </Typography>
                </Box>
                <Box className={styles.switchIconRight} id="forecast-view-toggle-graph-view-label">
                  <GraphBarIcon />
                  <Typography
                    component="span"
                    className={styles.switchText}
                    data-testid="graph-view-icon-text"
                  >
                    Graph
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
      {isHistoricView && (
        <div className={styles.datePickerMenuWrapper}>
          <DatePickerMenu
            closeButtonTooltipText="Return to current forecast"
            isPermitted={hasHistoricForecastPermissions}
            startDate={startDate}
            setStartDate={setStartDate}
            maxDate={maxDate}
            minDate={minDate}
            range={16}
            segmentPropertiesCTA={historicForecastSegmentCTAprops}
            selectButtonText="Select a forecast date"
            submitButtonText="View forecast"
            onClickClearButton={toggleHistoricView}
          />
        </div>
      )}
    </Box>
  );
};

export default memo(GraphHeader);
